import { cn } from "@healthos/ui";
import { Fragment, type ReactElement, type ReactNode } from "react";

// Entrance choreography shared by every page header: a staggered fade-in-up on
// `var(--ease-snap-spring)`, gated by the caller with `!skipEnter` and `motion-safe:`.
const HEADER_ENTER = "motion-safe:animate-[fade-in-up_280ms_var(--ease-snap-spring)_both]";

/**
 * Per-surface accent. Each page keeps its own identity colour (teal is the
 * house accent; groceries reads amber) while every other part of the header
 * stays identical across surfaces.
 */
export type PageHeaderAccent = "teal" | "amber";

const ACCENT: Record<PageHeaderAccent, { text: string; dot: string; emph: string }> = {
  // The house accent follows the page: --page-accent is the destination's own
  // hue (set per route in lib/page-accent), with brand teal where none is set.
  teal: {
    text: "text-ink-eyebrow",
    dot: "bg-[var(--page-accent,var(--color-teal-700))]",
    emph: "text-[var(--page-accent,var(--color-ink-brand))]",
  },
  amber: {
    text: "text-amber-ink",
    dot: "bg-amber-ink",
    emph: "text-amber-ink",
  },
};

/** The italic emphasis span inside a page header title, coloured by accent. */
export function PageHeaderEmph({
  accent = "teal",
  children,
}: {
  accent?: PageHeaderAccent;
  children: ReactNode;
}): ReactElement {
  return <em className={cn("italic", ACCENT[accent].emph)}>{children}</em>;
}

/**
 * The shared page header: an eyebrow (small sentence-case kicker with a
 * leading dot), a title, and a subtitle on one recipe so every surface reads the same;
 * only the accent changes per surface. Callers render any sub-navigation just
 * beneath this. Optional children sit under the subtitle for trailing chrome
 * such as the weight & doses tracking chips; `trailing` sits to the right of
 * the header text for page actions such as a button or search box.
 */
export function PageSectionHeader({
  accent = "teal",
  as: Heading = "h2",
  eyebrow,
  title,
  subtitle,
  skipEnter = false,
  reserveSubtitleSpace = false,
  className,
  trailing,
  children,
}: {
  accent?: PageHeaderAccent;
  /**
   * Heading element. Standalone pages whose header is the page title use
   * "h1"; surfaces that already render a page-level h1 (the plan tabs under
   * plan-screen's sr-only h1) keep the default "h2".
   */
  as?: "h1" | "h2";
  /**
   * Eyebrow segments, joined with hairline separators. Usually strings; a
   * segment may be a node when it carries its own styling (e.g. the Today
   * crumb's navy plan anchor).
   */
  eyebrow: readonly ReactNode[];
  title: ReactNode;
  /**
   * Optional supporting line. Omit it rather than restating the title: a
   * subtitle that says the same thing twice costs a row and, per NN/g, makes
   * both it and the title read as less important.
   */
  subtitle?: ReactNode;
  /** True when the page-entrance transition already played; skip the keyframe. */
  skipEnter?: boolean;
  /**
   * Reserve ~3 lines of subtitle height so sibling surfaces sharing this
   * header slot (the plan tabs) don't shift the content below on switch.
   */
  reserveSubtitleSpace?: boolean;
  /** Extra classes on the header element (e.g. the "pv-head" view-transition hook). */
  className?: string;
  /**
   * Right-aligned chrome beside the header text (an action button, a search
   * box). Bottom-aligned with the subtitle from the lg breakpoint; stacks
   * beneath the text on smaller screens. Size it in the caller.
   */
  trailing?: ReactNode;
  /** Trailing content beneath the subtitle (e.g. tracking chips). */
  children?: ReactNode;
}): ReactElement {
  const tone = ACCENT[accent];
  const body = (
    <div className="min-w-0">
      <div className={cn("mb-2 flex items-center gap-2 font-sans font-medium text-xs", tone.text)}>
        <span
          aria-hidden="true"
          className={cn("inline-block h-[5px] w-[5px] rounded-full", tone.dot)}
        />
        {eyebrow.map((segment, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are a fixed positional list, never reordered
          <Fragment key={index}>
            {index > 0 ? (
              <span className="text-ink-quaternary" aria-hidden="true">
                ·
              </span>
            ) : null}
            <span>{segment}</span>
          </Fragment>
        ))}
      </div>
      <Heading className="m-0 max-w-[43ch] font-title text-page-title font-medium leading-[1.2] tracking-tight text-ink-primary">
        {title}
      </Heading>
      {subtitle ? (
        <p
          className={cn(
            "m-0 mt-3 max-w-[65ch] text-lede leading-relaxed text-ink-secondary",
            reserveSubtitleSpace && "min-h-[3.25em]",
          )}
        >
          {subtitle}
        </p>
      ) : null}
      {children}
    </div>
  );
  return (
    <header className={cn("mb-6", !skipEnter && HEADER_ENTER, className)}>
      {trailing ? (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          {body}
          <div className="lg:shrink-0">{trailing}</div>
        </div>
      ) : (
        body
      )}
    </header>
  );
}

/**
 * The compact header for plan sub-pages (Long view, Groceries, Weight & doses,
 * Your Progress). These sit beneath the shared day header and the tab bar, so
 * they carry no eyebrow and a subordinate title, half the weight of the primary
 * PageSectionHeader. Same trailing / children slots for page actions and chips.
 */
export function PlanSubheader({
  as: Heading = "h2",
  title,
  subtitle,
  skipEnter = false,
  reserveSubtitleSpace = false,
  className,
  trailing,
  children,
}: {
  as?: "h1" | "h2";
  title: ReactNode;
  subtitle?: ReactNode;
  skipEnter?: boolean;
  reserveSubtitleSpace?: boolean;
  className?: string;
  trailing?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  const body = (
    <div className="min-w-0">
      <Heading className="m-0 max-w-[68ch] font-title text-[1.625rem] font-medium leading-snug tracking-tight text-ink-primary">
        {title}
      </Heading>
      {subtitle ? (
        <p
          className={cn(
            "m-0 mt-2 max-w-[80ch] text-sm leading-relaxed text-ink-tertiary",
            reserveSubtitleSpace && "min-h-[2.75em]",
          )}
        >
          {subtitle}
        </p>
      ) : null}
      {children}
    </div>
  );
  return (
    <header className={cn("mb-6", !skipEnter && HEADER_ENTER, className)}>
      {trailing ? (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          {body}
          <div className="lg:shrink-0">{trailing}</div>
        </div>
      ) : (
        body
      )}
    </header>
  );
}
